<template>
  <div class="brand-auth-page">
    <div class="page-toolbar">
      <el-button icon="el-icon-arrow-left" @click="goBack">Back</el-button>
      <el-button type="primary" @click="exportPDF">Export PDF</el-button>
    </div>
    <div class="wrap" id="brandAuthPrint" v-loading="loading">
      <header class="doc-head">
        <div>
          <h1>Brand Asset Authorization</h1>
          <p>{{ programName }} — Logo &amp; Brand Usage License</p>
        </div>
      </header>

      <div class="meta-grid">
        <div class="meta-card">
          <div class="k">Authorized School (Account)</div>
          <div class="v">{{ accountName }}</div>
        </div>
        <div class="meta-card">
          <div class="k">Issue Date (payment completed)</div>
          <div class="v">{{ paymentDate }}</div>
        </div>
        <div class="meta-card">
          <div class="k">Effective Start Date</div>
          <div class="v">{{ paymentDate }}</div>
        </div>
        <div class="meta-card">
          <div class="k">Effective End Date (project timeline)</div>
          <div class="v">{{ timelineEndDate }}</div>
        </div>
      </div>

      <section>
        <h2>1. Authorization Grant</h2>
        <p><strong>Grantor:</strong> {{ grantorName }}.</p>
        <p><strong>Grantee:</strong> {{ accountName }} ("Authorized School").</p>
        <p>
          <strong>Purpose:</strong> This authorization grants {{ accountName }} a limited, non-exclusive, non-transferable right
          to use the specified brand assets <strong>solely</strong> for promoting and conducting a
          <strong>{{ arrangementLabel }}</strong> event on school premises, from {{ paymentDate }} to {{ timelineEndDate }}
          (the "Effective Period").
        </p>
      </section>

      <section>
        <h2>2. Authorized Assets</h2>
        <p>The following brand assets are authorized for use under this document. Downloads unlock after confirmation below.</p>
        <el-table :data="assets" border style="width: 100%; margin-top: 12px;">
          <el-table-column prop="type" label="Type" width="120" />
          <el-table-column prop="name" label="Asset" min-width="180" />
          <el-table-column prop="fileName" label="File name" min-width="160" />
          <el-table-column label="Download" width="140" class-name="no-print">
            <template slot-scope="scope">
              <el-button
                size="mini"
                type="primary"
                plain
                :disabled="!confirmed"
                @click="downloadAsset(scope.row)">
                Download
              </el-button>
            </template>
          </el-table-column>
        </el-table>
      </section>

      <section>
        <h2>3. Permitted Uses</h2>
        <ul>
          <li><strong>On-campus promotion:</strong> posters, banners, signage, digital displays, student handouts and flyers announcing the School Day.</li>
          <li><strong>Event-day materials:</strong> programs, schedules, participant guides, name tags/badges, ceremony slides, and certificates of participation.</li>
          <li><strong>Internal reporting:</strong> summaries to school administration, parent committees, or school boards; event photos where logos appear incidentally.</li>
          <li><strong>Social media (restricted):</strong> up to three (3) posts per platform announcing or summarizing the event, each including required attribution.</li>
        </ul>
      </section>

      <section>
        <h2>4. Usage Guidelines</h2>
        <ul>
          <li><strong>No distortion:</strong> do not stretch, squeeze, rotate (except 90° steps), skew, or deform.</li>
          <li><strong>No color change:</strong> use only the original color versions provided.</li>
          <li><strong>No modification:</strong> do not add, remove, or alter any element.</li>
          <li><strong>No effects:</strong> no shadows, glows, outlines, 3D, or embossing.</li>
          <li><strong>Clear space &amp; contrast:</strong> keep padding and place logos only on sufficient-contrast backgrounds.</li>
        </ul>
      </section>

      <section>
        <h2>5. Confirmation</h2>
        <div class="confirm-box">
          <el-checkbox v-model="checks.c1" class="check-item">
            I confirm {{ accountName }} has read and agrees to the terms of this Brand Asset Authorization.
          </el-checkbox>
          <el-checkbox v-model="checks.c2" class="check-item">
            I confirm the authorized assets will be used solely for the {{ arrangementLabel }} event.
          </el-checkbox>
          <el-checkbox v-model="checks.c3" class="check-item">
            I confirm the school will comply with all usage guidelines and restrictions.
          </el-checkbox>
          <div class="confirm-actions no-print">
            <el-button type="primary" :disabled="!allChecked" @click="confirmRecord">Confirm &amp; Record</el-button>
            <span v-if="confirmed" class="ok">Confirmed — logo downloads are unlocked.</span>
          </div>
          <p v-if="confirmed" class="ok print-only">Confirmed — authorization recorded.</p>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import { checkApplication, recordBrandAuthorization } from '../../api/eae'
import { getUserId } from '../../utils/store'
import { resolveAmSubjectTheme } from '../../utils/amSubjectTheme'

export default {
  name: 'BrandAuthorization',
  data() {
    return {
      loading: false,
      application: null,
      checks: { c1: false, c2: false, c3: false },
      confirmed: false,
      assets: [
        { type: 'Brand', name: 'Brand logo', fileName: 'brand-logo.png', url: '' },
        { type: 'Subject', name: 'Subject logo', fileName: 'subject-logo.png', url: '' }
      ]
    }
  },
  computed: {
    allChecked() {
      return this.checks.c1 && this.checks.c2 && this.checks.c3
    },
    accountName() {
      return (this.application && (this.application.account_name || this.application.account?.account_nameEN || this.application.account?.account_name)) || '{{ AccountName }}'
    },
    programName() {
      const p = this.application && this.application.project_info
      return (p && (p.project_ename || p.project_name)) || 'Program'
    },
    projectBu() {
      const bu = this.application && this.application.project_info && this.application.project_info.project_BU
      return bu ? String(bu).toUpperCase() : ''
    },
    grantorName() {
      if (this.projectBu === 'AM') return 'International Academic Marathon Association ("Academic Marathon" or "AM")'
      return 'Extracurricular Activity Excellence International ("EAE")'
    },
    arrangementLabel() {
      return (this.application && this.application.participation_arrangement) || 'School day'
    },
    paymentDate() {
      const app = this.application
      if (!app) return '{{ PaymentDate }}'
      if (app.institution_payment_time) {
        return this.formatDate(app.institution_payment_time)
      }
      if (app.application_payment_date) return String(app.application_payment_date)
      if (app.application_payment_time) return this.formatDate(app.application_payment_time)
      return '{{ PaymentDate }}'
    },
    timelineEndDate() {
      const p = this.application && this.application.project_info
      const manage = this.application && this.application.projectManage
      const end = (manage && (manage.end_date || manage.timeline_end)) || (p && (p.deadline_date || p.project_end_date))
      if (!end) return '{{ TimelineEndDate }}'
      return this.formatDate(end)
    }
  },
  mounted() {
    this.fetchData()
  },
  methods: {
    goBack() {
      if (window.history.length > 1) {
        this.$router.go(-1)
      } else {
        this.$router.push({ path: '/home/studentRegistrations' })
      }
    },
    formatDate(value) {
      if (!value) return ''
      let d
      if (typeof value === 'number' || /^\d+$/.test(String(value))) {
        const n = Number(value)
        d = new Date(String(value).length > 10 ? n : n * 1000)
      } else {
        d = new Date(value)
      }
      if (isNaN(d.getTime())) return String(value)
      return d.toISOString().slice(0, 10)
    },
    resolveAssets(project) {
      const brandUrl = this.projectBu === 'AM'
        ? require('../../assets/image/logo_am.png')
        : require('../../assets/image/header-logo.png')
      let subjectUrl = ''
      let subjectName = 'Subject logo'
      if (this.projectBu === 'AM') {
        const theme = resolveAmSubjectTheme(
          (project && (project.project_code || project.project_ename || project.project_name)) || '',
          this.programName
        )
        subjectName = theme.subject + ' logo'
        try {
          subjectUrl = require('../../assets/image/am_subject_logos/' + theme.logo)
        } catch (e) {
          subjectUrl = ''
        }
      } else if (project) {
        subjectUrl = project.project_logo || project.logo_url || project.project_pic || ''
        subjectName = (project.project_ename || project.project_name || 'Program') + ' logo'
      }
      return [
        { type: 'Brand', name: this.projectBu === 'AM' ? 'Academic Marathon brand logo' : 'EAE brand logo', fileName: 'brand-logo.png', url: brandUrl },
        { type: 'Subject', name: subjectName, fileName: 'subject-logo.png', url: subjectUrl }
      ]
    },
    fetchData() {
      const id = this.$route.query.application_id
      if (!id) {
        this.$message.warning('Missing application id')
        return
      }
      this.loading = true
      checkApplication(id).then((res) => {
        this.application = (res.data && res.data.data) || res.data || null
        const recorded = this.application && this.application.brand_authorization && this.application.brand_authorization.confirmed
        if (recorded) {
          this.confirmed = true
          this.checks = { c1: true, c2: true, c3: true }
        }
        const project = this.application && this.application.project_info
        this.assets = this.resolveAssets(project)
        if (this.application && this.application.application_status !== '已缴费') {
          this.$message.warning('Brand Authorization is available after institution payment is confirmed.')
        }
      }).catch(() => {
        this.$message.error('Failed to load authorization details')
      }).finally(() => {
        this.loading = false
      })
    },
    confirmRecord() {
      if (!this.allChecked) return
      const applicationId = this.$route.query.application_id
      recordBrandAuthorization({
        application_id: applicationId,
        confirmed_by: getUserId() || ''
      }).then((res) => {
        if (res.data && res.data.code === 0) {
          this.confirmed = true
          this.$message.success('Authorization recorded. You may download brand assets.')
        } else {
          this.$message.error((res.data && res.data.msg) || 'Failed to record authorization')
        }
      }).catch(() => {
        this.$message.error('Failed to record authorization')
      })
    },
    downloadAsset(row) {
      if (!this.confirmed) {
        this.$message.warning('Please confirm and record authorization before downloading.')
        return
      }
      if (!row.url) {
        this.$message.warning('Asset file is not configured for this program yet.')
        return
      }
      window.open(row.url, '_blank')
    },
    exportPDF() {
      const el = document.getElementById('brandAuthPrint')
      if (!el) {
        this.$message.warning('Document is still loading.')
        return
      }
      const style = document.createElement('style')
      style.innerHTML = '@page{size:A4 portrait;margin:12mm;} .no-print{display:none!important;} .print-only{display:block!important;}'
      window.document.head.appendChild(style)
      const printData = el.innerHTML
      const original = window.document.body.innerHTML
      window.document.body.innerHTML = '<div style="padding:16px;font-family:Arial,sans-serif;">' + printData + '</div>'
      window.print()
      window.document.body.innerHTML = original
      window.location.reload()
    }
  }
}
</script>

<style scoped>
.brand-auth-page {
  background: #F5F5F5;
  min-height: 100vh;
  padding: 24px 16px 48px;
}
.page-toolbar {
  max-width: 920px;
  margin: 0 auto 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.wrap {
  max-width: 920px;
  margin: 0 auto;
  background: #fff;
  padding: 32px 40px 48px;
  box-shadow: 0 2px 16px rgba(0,0,0,.06);
}
.print-only {
  display: none;
}
.doc-head h1 {
  margin: 0;
  color: #1B3B6F;
  font-size: 1.55rem;
}
.doc-head p {
  margin: 4px 0 24px;
  color: #757575;
}
.meta-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  margin: 20px 0 28px;
}
.meta-card {
  background: #F5F5F5;
  border: 1px solid #E0E0E0;
  border-radius: 8px;
  padding: 12px 16px;
}
.meta-card .k {
  font-size: .72rem;
  text-transform: uppercase;
  letter-spacing: .04em;
  color: #757575;
}
.meta-card .v {
  font-size: 1.05rem;
  font-weight: 600;
  color: #1B3B6F;
  margin-top: 2px;
}
section {
  margin: 30px 0;
}
section > h2 {
  color: #1B3B6F;
  font-size: 1.2rem;
  border-left: 4px solid #D4AF37;
  padding-left: 10px;
  margin: 0 0 14px;
}
.confirm-box {
  border: 2px dashed #1B3B6F;
  border-radius: 12px;
  padding: 22px 26px;
  background: #fafbff;
}
.check-item {
  display: flex;
  margin: 12px 0;
  white-space: normal;
  align-items: flex-start;
}
.confirm-actions {
  margin-top: 18px;
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
}
.ok {
  color: #2E7D32;
  font-weight: 600;
}
/deep/ .el-table .cell {
  padding: 10px 12px;
}
/deep/ .el-table th > .cell {
  padding: 10px 12px;
}
@media (max-width: 640px) {
  .wrap { padding: 20px 16px 40px; }
  .meta-grid { grid-template-columns: 1fr; }
  .page-toolbar { flex-wrap: wrap; gap: 8px; }
}
</style>
